import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { RestfulEndpoint } from '../../../services/restful-endpoint.service';
import { ToastrService } from 'ngx-toastr';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { download, Download } from '../../../shared/classes/download';
import { SAVER, Saver } from '../../../shared/classes/saver.provider';
import {
  FileCategory,
  FileLog,
  FilePermission,
  FilesListResponse,
  FileModel,
} from 'app/models/files.model';

@Injectable({
  providedIn: 'root',
})
export class FilesService {
  _fileLogs: FileLog[];

  constructor(
    private endPoint: RestfulEndpoint,
    protected httpClient: HttpClient,
    private _toaster: ToastrService,
    @Inject(SAVER) private save: Saver,
  ) {}

  private _permissions: FilePermission[];

  private _files: FileModel[] = [];
  filesSubject: BehaviorSubject<FileModel[]> = new BehaviorSubject<FileModel[]>([]);
  readonly files$: Observable<FileModel[]> = this.filesSubject.asObservable();

  get files(): FileModel[] {
    return this._files;
  }

  private static downloadUrl(url: string, fileName: string): void {
    const a: any = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.style = 'display: none';
    a.click();
    a.remove();
  }

  // fetchFiles
  async fetch(projectId: string, force: boolean = false): Promise<boolean> {
    if (force) {
      this._files = [];
    }
    if (!this._files.length) {
      const response: FilesListResponse = await this.endPoint
        .post('get_attached_files_project', { project_id: projectId })
        .toPromise();
      this._files = response.files_list;
      this._permissions = response.permissions;
      this._fileLogs = response.logs;

      response.files_list.forEach((responseFile) => {
        if (
          !responseFile.report_id ||
          responseFile.report_id === 'undefined' ||
          responseFile.report_id === '0'
        ) {
          responseFile.report_id = '0';
          responseFile.report_name = 'not set';
        }
        if (
          !responseFile.category_id ||
          responseFile.category_id === 'undefined' ||
          responseFile.category_id === '0'
        ) {
          responseFile.category_id = '0';
          responseFile.category_name = 'not set';
        }
      });

      this.filesSubject.next(this._files);
      return true;
    }
  }

  getById(fileId): FileModel {
    return this._files.find((file) => file.file_id === fileId);
  }

  getByType(type: 'project' | 'report' | 'category', id: string): FileModel[] {
    // project = all files in the project
    if (type === 'report') {
      return this._files.filter((file) => file.report_id === id);
    } else if (type === 'category') {
      return this._files.filter((file) => file.category_id === id);
    } else {
      return this._files;
    }
  }

  async uploadFile(
    attached: any[],
    projectId: string,
    reportId?: string,
    categoryId?: string,
  ): Promise<boolean> {
    const formData = new FormData();

    formData.append('type', reportId === '0' ? 'project' : 'report');
    formData.append('project_id', projectId);
    attached.forEach((el) => {
      if (el.name.endsWith('.kmz')) {
        const file = new File([el], el.name, { type: 'application/vnd.google-earth.kmz' });
        formData.append('attached[]', file);
      } else {
        formData.append('attached[]', el);
      }
    });

    if (reportId !== '0') {
      formData.append('report_id', reportId);
    }
    if (categoryId !== '0') {
      formData.append('category_id', categoryId);
    }

    const response: { status: boolean } = await this.endPoint
      .post('create_attached_files', formData)
      .toPromise();
    if (response?.status) {
      await this.fetch(projectId, true);
      return true;
    }
  }

  downloadLoader(url: string, filename: string): Observable<Download> {
    return this.httpClient
      .get(url, {
        reportProgress: true,
        observe: 'events',
        responseType: 'blob',
      })
      .pipe(download((blob) => this.save(blob, filename)));
  }

  download(filePath: string, filename: string): void {
    this.httpClient.get(filePath, { responseType: 'blob' }).subscribe((val) => {
      const url = URL.createObjectURL(val);
      FilesService.downloadUrl(url, filename);
      URL.revokeObjectURL(url);
    });
  }
  blob(url: string, filename?: string): Observable<Blob> {
    return this.httpClient.get(url, {
      responseType: 'blob',
    });
  }

  reset(): void {
    this._files = [];
    this._permissions = [];
    this.filesSubject.next(this._files);
  }

  async moveToCategory(fileId: string, category: FileCategory): Promise<boolean> {
    const moveToBody = {
      file_id: fileId,
      category_id: category.id,
    };
    const response: { success: boolean } = await this.endPoint
      .post('move_to_category', moveToBody)
      .toPromise();
    if (response?.success) {
      const movedFile = this.files.find((file) => file.file_id === fileId);
      if (movedFile) {
        movedFile.category_id = category.id || '0';
        movedFile.category_name = category.category_name || 'not set';
        this.filesSubject.next(this.files);
      }
    } else {
      return false;
    }

    return true;
  }

  async uploadBIMFile(attachments: any[], project_id: string) {
    try {
      // Step 1: Get pre-signed URLs (this also creates the BIM file record with URN = null)
      const filesMetadata = attachments.map((file) => ({
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
      }));

      let urlResponse: {
        success: boolean;
        files: Array<{
          id: string;
          filename: string;
          content_type: string;
          upload_url: string;
          method: string;
          file_path: string;
        }>;
        errors?: string[];
      };

      try {
        urlResponse = await this.endPoint
          .post('upload_bim_file', { project_id, files: filesMetadata })
          .toPromise();
      } catch (error: any) {
        // Handle HTTP errors from the API
        if (error?.error?.errors && Array.isArray(error.error.errors)) {
          throw { errors: error.error.errors };
        }
        throw new Error(
          error?.error?.message || 'Failed to get pre-signed URLs. Please try again.',
        );
      }

      if (!urlResponse?.success || !urlResponse.files || urlResponse.files.length === 0) {
        throw new Error('Failed to get pre-signed URLs. Please try again.');
      }

      // Step 2: Upload files to OCI bucket using pre-signed URLs
      const uploadPromises = attachments.map(async (file, index) => {
        const uploadInfo = urlResponse.files[index];
        if (!uploadInfo) {
          throw new Error(`No upload information available for file: ${file.name}`);
        }

        try {
          const uploadResponse = await fetch(uploadInfo.upload_url, {
            method: uploadInfo.method || 'PUT',
            headers: {
              'Content-Type': uploadInfo.content_type,
            },
            body: file,
          });

          if (!uploadResponse.ok) {
            const errorText = await uploadResponse.text().catch(() => 'Unknown error');
            throw new Error(
              `Failed to upload file ${file.name} to storage. ${uploadResponse.status}: ${errorText}`,
            );
          }
        } catch (error) {
          if (error instanceof Error) {
            throw error;
          }
          throw new Error(`Failed to upload file ${file.name}: ${error}`);
        }
      });

      await Promise.all(uploadPromises);

      // Step 3: Create/trigger BIM file processing (background job will update URN)
      let createResponse: { success: boolean; file?: any; message?: string; errors?: string[] };
      try {
        createResponse = await this.endPoint.post('create_bim_file', { project_id }).toPromise();
      } catch (error: any) {
        // Handle HTTP errors from the API
        if (error?.error?.errors && Array.isArray(error.error.errors)) {
          throw { errors: error.error.errors };
        }
        throw new Error(
          error?.error?.message || 'Failed to initiate BIM file processing. Please try again.',
        );
      }

      if (createResponse?.success) {
        await this.fetch(project_id, true);
        // Return the response with message for displaying in toast
        return { success: true, message: createResponse.message };
      } else {
        throw new Error('Failed to initiate BIM file processing. Please try again.');
      }
    } catch (error) {
      console.error('Error uploading BIM file:', error);
      // Re-throw error as-is (could be Error instance or { errors: string[] })
      throw error;
    }
  }

  async uploadPhasesFile(attachments: any, project_id: string) {
    const formData = new FormData();
    formData.append('project_id', project_id);
    attachments.forEach((el) => {
      if (el.name.endsWith('.kmz')) {
        const file = new File([el], el.name, { type: 'application/vnd.google-earth.kmz' });
        formData.append('file', file);
      } else {
        formData.append('file', el);
      }
    });

    const response: { status: string } = await this.endPoint
      .post('import_phases', formData)
      .toPromise();
    if (response?.status === 'success') {
      await this.fetch(project_id, true);
      return true;
    }
  }

  async uploadProgressFile(attachments: any, project_id: string) {
    const formData = new FormData();
    attachments.forEach((el) => {
      if (el.name.endsWith('.kmz')) {
        const file = new File([el], el.name, { type: 'application/vnd.google-earth.kmz' });
        formData.append('file', file);
      } else {
        formData.append('file', el);
      }
    });

    const response: { status: string } = await this.endPoint
      .post('import_progress', formData)
      .toPromise();
    if (response?.status === 'success') {
      await this.fetch(project_id, true);
      return true;
    }
  }

  async deleteFile(id: string, project_id: string) {
    try {
      const response: { success: boolean } = await this.endPoint
        .post('delete_attached', { id, project_id })
        .toPromise();
      return response;
    } catch (error) {
      return error;
    }
  }
}
